/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/browser-apis/
 */

import React from 'react';
import posthog from 'posthog-js';
import {AppRoot, PageContainer} from './src/PageContainer';
import GlobalErrorBoundary from './src/Components/GlobalErrorBoundary';
import {NO_LAYOUT_PATHS, isNoLayoutPath} from './src/Utils/noLayoutPaths';
import './src/polyfill';

const config = require('./src/data.json');
if (config.posthogApiKey) {
  posthog.init(config.posthogApiKey, {
    api_host: config.posthogApiHost,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_exceptions: true,
  });
}

// ─── 設計編輯器 chunk 預載機制：白牌已移除 ───
//
// 原本此處有 PREFETCH_ELIGIBLE_PATHS + prefetchEditor()（idle 預載 8.9 MB 的
// konva-designer chunk，ChunkLoadError 最多重試 2 次）。白牌不提供編輯器
// （config.enableEditor === false），且 @sensationsprint/konva-print-designer
// 未安裝——**webpack 會在 build 期解析 import() 的模組字串，套件不存在即 build 失敗**，
// 所以不能只靠 runtime 條件包住，必須整段移除。
//
// 要恢復（客戶採購編輯器時）：
//   1. package.json 加回 @sensationsprint/konva-print-designer（私有 repo，需存取權）
//      與 konva / react-konva / @techstark/opencv-js / clipper-lib / simplify-js / html2canvas
//   2. configs/{env}/data.json 設 enableEditor: true
//   3. 自 frontend-lixiang production 的 gatsby-browser.js 取回本區塊、
//      onClientEntry 與 onRouteUpdate 內的 schedulePrefetchEditorIfEligible 呼叫

export const wrapPageElement = ({element, props}) => {
  // 與 gatsby-ssr.js 一致：用 location.pathname（真實 URL）判斷，
  // 避免 SSR 端 props.path 為 catch-all（'/*'）而 client 端為真實路徑造成的 hydration 不一致。
  const pathname = (props.location && props.location.pathname) || props.path;
  if (isNoLayoutPath(pathname)) {
    return element;
  }
  return <PageContainer {...props}>{element}</PageContainer>;
};

export const wrapRootElement = ({element}) => {
  return (
    <GlobalErrorBoundary>
      <AppRoot>{element}</AppRoot>
    </GlobalErrorBoundary>
  );
};

// Gatsby V5 相容的 onRouteUpdate（SPA 切換）
export const onRouteUpdate = ({location, prevLocation}) => {
  const widget = document.querySelector('div[script-id="UOmW9"]');
  if (widget) {
    if (NO_LAYOUT_PATHS.some((path) => location.pathname.includes(path))) {
      widget.style.display = 'none';
    } else {
      widget.style.display = 'block';
    }
  }

  // （編輯器 chunk 預載呼叫已隨白牌移除，見檔頭說明）

  if (process.env.NODE_ENV !== 'production') {
    console.log('路由更新:', location.pathname);
    console.log('前一個路由:', prevLocation ? prevLocation.pathname : null);
  }
};

// React 18 相容性設定 + 最後防線事件攔截（僅 production 啟用）
export const onClientEntry = () => {
  if (typeof window !== 'undefined') {
    window.__GATSBY_INITIAL_PROPS = window.__GATSBY_INITIAL_PROPS || {};

    // （編輯器 chunk 預載呼叫已隨白牌移除，見檔頭說明）

    // 已移除全域 error / unhandledrejection 的強制跳轉 /404（放大鏈根因之一）。
    // 未處理錯誤改由 React ErrorBoundary 顯示 fallback，或保持在原頁；
    // PostHog SDK（capture_exceptions: true）會自動 capture，不需要在此導頁。
  }
};
